import { useQuery } from "@tanstack/react-query";
import { Download, Palette, Pencil, RefreshCw, Share2 } from "lucide-react";
import type { JSX } from "react";
import { Suspense, lazy, useState } from "react";

import { DraftDiffDisclosure } from "@/components/ui/draft-diff";
import { useToast } from "@/components/ui/toast-provider";
import type { ReportPointer, ReportRevisionRead } from "@/lib/reports";
import {
  createOwnerReportImageFetcher,
  downloadOwnerPdfBlob,
  getOwnerPdfStatus,
  getReportRevision,
  pollUntilPdfReady,
  requestOwnerPdf,
  triggerBlobDownload,
} from "@/lib/reports";

import { ExecutiveReport, type ExecutiveReportMode } from "./ExecutiveReport";
import { ShareDialog } from "./ShareDialog";
import type { ReportThemeOverride } from "./report-theme";

// The comparison with the previous revision (and the text projection of a revision it
// reads) is only needed once its closed disclosure is opened, so it is code-split out of
// the chunk the reader is in, like the chart of a report is.
const PreviousRevisionChanges = lazy(() =>
  import("./PreviousRevisionChanges").then((module) => ({
    default: module.PreviousRevisionChanges,
  })),
);

export function reportRevisionQueryKey(
  sessionId: string,
  reportId: string,
  revisionNumber: number,
): readonly ["reports", string, string, number] {
  return ["reports", sessionId, reportId, revisionNumber];
}

interface ReportReaderProps {
  sessionId: string;
  pointer: ReportPointer;
  mode: ExecutiveReportMode;
  onModify: (read: ReportRevisionRead) => void;
  onOpenRevision?: (pointer: ReportPointer) => void;
}

function statusOf(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("status" in error)) return null;
  return typeof error.status === "number" ? error.status : null;
}

export function ReportReader({
  sessionId,
  pointer,
  mode,
  onModify,
  onOpenRevision,
}: ReportReaderProps): JSX.Element {
  const { showToast } = useToast();
  const [theme, setTheme] = useState<ReportThemeOverride>("active");
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfMessage, setPdfMessage] = useState("");
  const query = useQuery({
    queryKey: reportRevisionQueryKey(sessionId, pointer.report_id, pointer.revision_number),
    queryFn: () =>
      getReportRevision({
        sessionId,
        reportId: pointer.report_id,
        revisionNumber: pointer.revision_number,
      }),
    retry: false,
  });

  async function handlePdf(): Promise<void> {
    setPdfMessage("Preparando PDF…");
    try {
      const requested = await requestOwnerPdf({
        sessionId,
        reportId: pointer.report_id,
        revisionNumber: pointer.revision_number,
      });
      const state = await pollUntilPdfReady(() =>
        getOwnerPdfStatus({
          sessionId,
          reportId: pointer.report_id,
          revisionNumber: pointer.revision_number,
          exportId: requested.export_id,
        }),
      );
      if (state.status !== "ready") {
        setPdfMessage("");
        showToast({ kind: "error", message: "No se pudo generar el PDF. Inténtalo de nuevo." });
        return;
      }
      // Auth is Bearer-only — top-level navigation cannot attach Authorization.
      const blob = await downloadOwnerPdfBlob({
        sessionId,
        reportId: pointer.report_id,
        revisionNumber: pointer.revision_number,
        exportId: state.export_id,
      });
      triggerBlobDownload(blob, `report-v${pointer.revision_number}.pdf`);
      setPdfMessage("");
      showToast({ kind: "success", message: "PDF descargado." });
    } catch {
      setPdfMessage("");
      showToast({ kind: "error", message: "No se pudo generar el PDF." });
    }
  }

  if (query.isPending) {
    return <p className="py-10 text-center text-body-3 text-muted-foreground">Cargando informe…</p>;
  }
  if (query.isError) {
    const hidden = statusOf(query.error) === 404;
    return (
      <div className="mx-auto max-w-md space-y-3 py-10 text-center">
        <p className="text-body-2 font-medium">No se pudo abrir este informe.</p>
        {!hidden ? (
          <button
            aria-label="Reintentar"
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-body-3 hover:bg-muted"
            onClick={() => void query.refetch()}
            type="button"
          >
            <RefreshCw size={15} /> Reintentar
          </button>
        ) : null}
      </div>
    );
  }

  const read = query.data;
  const latestKnownRevision = Math.max(
    pointer.revision_number,
    read.newer_revision?.revision_number ?? pointer.revision_number,
  );
  return (
    <div className="min-h-full">
      <div className="sticky top-0 z-20 flex flex-wrap items-center justify-end gap-2 border-b border-border bg-background/95 px-3 py-2 backdrop-blur">
        <label className="inline-flex items-center gap-1.5 text-body-3 text-muted-foreground">
          Revisión
          <select
            aria-label="Revisión"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-foreground"
            disabled={!onOpenRevision}
            onChange={(event) =>
              onOpenRevision?.({
                report_id: pointer.report_id,
                revision_number: Number(event.target.value),
              })
            }
            value={pointer.revision_number}
          >
            {Array.from({ length: latestKnownRevision }, (_, index) => index + 1).map(
              (revision) => (
                <option key={revision} value={revision}>
                  v{revision}
                </option>
              ),
            )}
          </select>
        </label>
        <button
          aria-label={theme === "active" ? "Vista BeFlow Light" : "Usar tema actual"}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-body-3 hover:bg-muted"
          onClick={() => setTheme((current) => (current === "active" ? "beflow-light" : "active"))}
          type="button"
        >
          <Palette size={15} /> {theme === "active" ? "BeFlow Light" : "Tema actual"}
        </button>
        <button
          aria-label="Compartir informe"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-body-3 hover:bg-muted"
          onClick={() => setShareOpen(true)}
          type="button"
        >
          <Share2 size={15} /> Compartir
        </button>
        <button
          aria-label="Descargar PDF"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-body-3 hover:bg-muted"
          onClick={() => void handlePdf()}
          type="button"
        >
          <Download size={15} /> PDF
        </button>
        <button
          aria-label="Modificar informe"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-extra-2-40 px-2.5 py-1.5 text-body-3 font-semibold text-white hover:opacity-90"
          onClick={() => onModify(read)}
          type="button"
        >
          <Pencil size={15} /> Modificar
        </button>
      </div>
      {pdfMessage ? (
        <p
          aria-live="polite"
          className="border-b border-border px-3 py-2 text-body-3 text-muted-foreground"
        >
          {pdfMessage}
        </p>
      ) : null}
      {pointer.revision_number > 1 ? (
        <DraftDiffDisclosure
          // The chat dock keeps one reader for every report: a disclosure belongs to one report's
          // revision and the next one starts closed, with nothing read for it.
          key={`${pointer.report_id}:${pointer.revision_number}`}
          className="border-b border-border px-3 text-body-3"
          summary={`Ver cambios respecto de v${pointer.revision_number - 1}`}
          summaryClassName="py-2"
        >
          <Suspense
            fallback={
              <p aria-live="polite" className="pb-3 text-help text-muted-foreground">
                Cargando…
              </p>
            }
          >
            <PreviousRevisionChanges
              sessionId={sessionId}
              pointer={pointer}
              current={read.revision}
              queryKey={reportRevisionQueryKey(
                sessionId,
                pointer.report_id,
                pointer.revision_number - 1,
              )}
            />
          </Suspense>
        </DraftDiffDisclosure>
      ) : null}
      <ExecutiveReport
        mode={mode}
        read={read}
        themeOverride={theme}
        fetchImageBlob={createOwnerReportImageFetcher({
          sessionId,
          reportId: pointer.report_id,
          revisionNumber: pointer.revision_number,
        })}
        {...(onOpenRevision
          ? {
              onOpenRevision: (next) =>
                onOpenRevision({
                  report_id: next.report_id,
                  revision_number: next.revision_number,
                }),
            }
          : {})}
      />
      <ShareDialog
        onClose={() => setShareOpen(false)}
        open={shareOpen}
        reportId={pointer.report_id}
        revisionNumber={pointer.revision_number}
        sessionId={sessionId}
      />
    </div>
  );
}
